import type {
  HttpTransport,
  TransportRequestConfig,
} from '@mangovoice/api-client'
import type { components } from '@mangovoice/types/generated/mango-api'

export type QueueAgentStatus =
  components['schemas']['ExtensionCallCenterAgentStatus']

export type UserAgentEventLogType = 'agent_status' | 'queue' | 'user'

/**
 * Event-log row used by mango-api and the CCUA gateway mapper.
 *
 * mango-api's generated `CallCenterUserAgentEventLog` omits `event_type` and
 * marks several fields optional; Django still returns int `user_id` /
 * `initiated_by_user_id` plus `event_type` and `agent_availability`. Gateway
 * rows use `user_uuid` / `initiated_by_user_uuid` instead, so the mango-api
 * identity fields are optional on this shared type. Gateway rows may also
 * include `initiated_by_name`, resolved by call-center-agents at read time.
 */
export interface UserAgentEventLog {
  id: number | string
  event_type?: UserAgentEventLogType
  agent_status: string
  agent_availability?: string
  call_center_user_agent_id?: number | string
  extension_uuid: string
  pbx_id?: number
  user_id?: number
  user_uuid?: string | null
  initiated_by_user_id?: number
  initiated_by_user_uuid?: string | null
  initiated_by_name?: string | null
  created_at: string
  logged_in: boolean
}

export function createQueuesResource(transport: HttpTransport) {
  return {
    getQueueAgentStatus: (extensionId: number) =>
      transport.get<QueueAgentStatus>(
        `/app/v1/extensions/${extensionId}/call-queues/agent/`,
      ),

    putQueueLogin: (extensionId: number) =>
      transport.put<void>(
        `/app/v1/extensions/${extensionId}/call-queues/log-in/`,
      ),

    putQueueLogout: (extensionId: number) =>
      transport.put<void>(
        `/app/v1/extensions/${extensionId}/call-queues/log-out/`,
      ),

    getQueueDashboardQueues: (pbxId: number, config?: TransportRequestConfig) =>
      transport.get<unknown>(
        '/app/v1/extensions/',
        { pbx_id: pbxId, type: 'ExtensionCallQueue' },
        config,
      ),

    getQueueDashboardCalls: (
      pbxUuid: string,
      queueNumber: string,
      config?: TransportRequestConfig,
    ) =>
      transport.get<unknown>(
        '/app/v1/call-center-queues/members/',
        { pbx_uuid: pbxUuid, number: queueNumber },
        config,
      ),

    getQueueDashboardAgents: (
      pbxUuid: string,
      queueNumber: string,
      config?: TransportRequestConfig,
    ) =>
      transport.get<unknown>(
        '/app/v1/call-center-queues/agents/',
        { pbx_uuid: pbxUuid, number: queueNumber },
        config,
      ),

    getQueueDashboardStats: (
      pbxId: number,
      queueId: string,
      minutes: number,
      config?: TransportRequestConfig,
    ) =>
      transport.get<unknown>(
        `/api/pbx/${pbxId}/queues/${queueId}/stats?minutes=${minutes}`,
        undefined,
        config,
      ),

    postQueueDashboardAgentLogin: (agentId: string) =>
      transport.post<{ success: boolean }>(`/api/agents/${agentId}/login`),

    postQueueDashboardAgentLogout: (agentId: string) =>
      transport.post<{ success: boolean }>(`/api/agents/${agentId}/logout`),
  }
}
